import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { useLang, swapLangInPath } from '@/lib/i18n';
import { cn } from '@/lib/utils';

interface Props {
  className?: string;
  variant?: 'header' | 'footer';
}

/**
 * DE/EN toggle. Navigates to the same logical route with the opposite
 * language prefix. Persists the chosen language in localStorage so a
 * direct visit to `/` can still respect a returning user's choice if
 * future routing decides to read it (kept purely informational for now —
 * we never auto-redirect, per UX decision).
 */
export function LanguageSwitcher({ className, variant = 'header' }: Props) {
  const lang = useLang();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  const switchTo = (target: 'de' | 'en') => {
    if (target === lang) return;
    const next = swapLangInPath(pathname, target);
    try {
      localStorage.setItem('fernau-lang', target);
    } catch {
      /* ignore storage errors */
    }
    navigate(`${next}${search}${hash}`);
  };

  const base =
    variant === 'header'
      ? 'inline-flex items-center gap-1.5 rounded-md border border-border/40 bg-secondary/40 p-0.5 text-xs font-mono'
      : 'inline-flex items-center gap-1.5 rounded-md border border-border/40 p-0.5 text-xs font-mono';

  return (
    <div className={cn(base, className)} role="group" aria-label="Language / Sprache">
      <Globe className="w-3.5 h-3.5 mx-1 text-muted-foreground" aria-hidden="true" />
      <button
        type="button"
        onClick={() => switchTo('de')}
        aria-pressed={lang === 'de'}
        aria-label="Auf Deutsch wechseln"
        className={cn(
          'px-2 py-1 rounded transition-colors uppercase tracking-wider',
          lang === 'de'
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        DE
      </button>
      <button
        type="button"
        onClick={() => switchTo('en')}
        aria-pressed={lang === 'en'}
        aria-label="Switch to English"
        className={cn(
          'px-2 py-1 rounded transition-colors uppercase tracking-wider',
          lang === 'en'
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        EN
      </button>
    </div>
  );
}